import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from '@/components/navigation/Sidebar'
import Header from '@/components/navigation/Header'
import { useLocalStorageToggle } from '@/hooks/useLocalStorage'
import { STORAGE_KEYS } from '@/lib/storage'
import { cn } from '@/lib/utils'

/**
 * D7 (runbook 4): pàgines que ocupen exactament la finestra i fan scroll per dins (l'inbox de
 * WhatsApp i el calendari v2). A mòbil `h-screen` (100vh) és més alt que la finestra visible
 * (100dvh) i el `<main>` feia scroll a més del fil: dues barres. Per a la resta, res no canvia.
 *
 * El calendari v2 (`/calendar-v2` i `/reserves/:ids`, P12 de `plans/calendar-visual-refresh.md`):
 * la pàgina era `h-screen` dins d'aquest `<main>` (que ja té la capçalera de 64 px i el padding a
 * sobre) i el `<main>` feia scroll i s'enduia la barra de navegació del calendari. Ara la pàgina és
 * `h-full` i només la graella fa scroll. `/reserves` sol (la llista) no hi entra. El calendari
 * històric (`/calendari/historic`) tenia el mateix problema i va igual.
 */
const isFullHeightRoute = (pathname: string) =>
  /^\/(whatsapp|calendar-v2|calendari\/historic)(\/|$)/.test(pathname) ||
  /^\/reserves\/[^/]+/.test(pathname)

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarMinimized, toggleSidebarMinimized] = useLocalStorageToggle(
    STORAGE_KEYS.MAIN_SIDEBAR_COLLAPSED,
    false
  )
  const location = useLocation()

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Close mobile sidebar on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fullHeight = isFullHeightRoute(location.pathname)

  return (
    <div className={cn('flex bg-gray-50 overflow-hidden', fullHeight ? 'h-[100dvh]' : 'h-screen')}>
      {/* Sidebar */}
      <Sidebar
        open={sidebarOpen}
        minimized={sidebarMinimized}
        onClose={() => setSidebarOpen(false)}
        onToggleMinimize={toggleSidebarMinimized}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

        {/* Page content */}
        <main className={cn('flex-1', fullHeight ? 'min-h-0 overflow-hidden' : 'overflow-y-auto')}>
          <div className={cn('p-0 md:p-4', fullHeight && 'h-full')}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
